export interface AccountItem {
  id?: number;
  company_id?: number;
  code: string;
  name: string;
  type: string;
  level: number;
  parent_id?: number | null;
  nature: 'DEBIT' | 'CREDIT';
  requires_third_party: boolean;
  requires_cost_center: boolean;
  is_active: boolean;
  parent_code?: string;
  parent_name?: string;
}

export interface JournalEntryLine {
  id?: number;
  entry_id?: number;
  account_id: number;
  account_code?: string;
  account_name?: string;
  contact_id?: number | null;
  contact_name?: string;
  cost_center_id?: number | null;
  description: string;
  debit: number;
  credit: number;
  base_amount?: number;
}

export interface JournalEntry {
  id: number;
  company_id: number;
  entry_number: string;
  number?: string;
  entry_type?: string;
  entry_date: string;
  date?: string;
  concept: string;
  description?: string;
  reference_type?: string;
  reference_id?: number;
  reference_uid?: string;
  is_adjustment?: boolean;
  status: 'DRAFT' | 'POSTED' | 'ANNULLED';
  created_at: string;
  total_debit: number;
  total_credit: number;
  items: JournalEntryLine[];
  lines?: JournalEntryLine[];
}

export interface TrialBalanceItem {
  account_id: number;
  code: string;
  name: string;
  level: number;
  type: string;
  nature: 'DEBIT' | 'CREDIT';
  initial_debit: number;
  initial_credit: number;
  initial_balance: number;
  period_debit: number;
  period_credit: number;
  final_debit: number;
  final_credit: number;
  final_balance: number;
}

export interface TrialBalanceResponse {
  company_id?: number;
  start_date: string | null;
  end_date: string | null;
  is_balanced?: boolean;
  difference?: number;
  accounts: TrialBalanceItem[];
  totals: {
    initial_debit: number;
    initial_credit: number;
    period_debit: number;
    period_credit: number;
    final_debit: number;
    final_credit: number;
    is_balanced: boolean;
    difference: number;
  };
}

export interface GeneralLedgerMovement {
  id?: number;
  entry_id: number;
  entry_number: string;
  entry_type?: string;
  entry_date: string;
  date?: string;
  concept: string;
  line_description?: string;
  contact_id?: number | null;
  contact_name?: string;
  contact_document?: string;
  debit: number;
  credit: number;
  base_amount?: number;
  balance: number;
  running_balance?: number;
}

export interface GeneralLedgerAccount {
  id?: number;
  account_id?: number;
  code: string;
  name: string;
  type?: string;
  nature: 'DEBIT' | 'CREDIT';
  level?: number;
  initial_balance?: number;
  total_debit?: number;
  total_credit?: number;
  final_balance?: number;
  movements?: GeneralLedgerMovement[];
}

export interface GeneralLedgerReport {
  account: {
    id: number;
    code: string;
    name: string;
    type?: string;
    nature: 'DEBIT' | 'CREDIT';
    level?: number;
  };
  start_date: string | null;
  end_date: string | null;
  initial_balance: number;
  total_debit: number;
  total_credit: number;
  final_balance: number;
  movements: GeneralLedgerMovement[];
  ledger?: any[];
}

export type GeneralLedgerResponse = GeneralLedgerReport;

export interface IncomeStatementAccountItem {
  account_id?: number;
  code: string;
  name: string;
  balance?: number;
  net_amount?: number;
  nature?: 'DEBIT' | 'CREDIT';
}

export interface IncomeStatementResponse {
  company_id?: number;
  start_date: string | null;
  end_date: string | null;
  revenues: {
    accounts: IncomeStatementAccountItem[];
    total: number;
  };
  costs: {
    accounts: IncomeStatementAccountItem[];
    total: number;
  };
  production_costs: {
    accounts: IncomeStatementAccountItem[];
    total: number;
  };
  gross_profit: number;
  gross_margin_percentage: number;
  expenses: {
    accounts: IncomeStatementAccountItem[];
    total: number;
  };
  operating_income: number;
  operating_margin_percentage: number;
}

export interface AccountingConfigItem {
  id?: number;
  company_id: number;
  account_key?: string;
  config_key?: string;
  account_id: number;
  accounting_accounts?: {
    id: number;
    code: string;
    name: string;
    type: string;
    nature: string;
    level: number;
  };
  account_code?: string;
  account_name?: string;
  description?: string;
}

export interface AccountingPeriodLock {
  id?: number;
  company_id: number;
  lock_date: string | null;
  tax_lock_date?: string | null;
  locked_by_user_id?: number | null;
  notes?: string | null;
  updated_at?: string | null;
}

export interface AccountingCompanySettings {
  company_id: number;
  auto_accounting_enabled: boolean;
  updated_by?: number | null;
  updated_at?: string | null;
}


export type TreasuryType = 'CASH' | 'BANK' | 'WALLET' | 'CREDIT_CARD' | 'PARTNER';

export interface TreasuryAccountItem {
  id?: number;
  company_id: number;
  name: string;
  type: TreasuryType;
  bank_name?: string | null;
  account_number?: string | null;
  puc_account_id?: number | null;
  puc_account?: {
    id: number;
    code: string;
    name: string;
  } | null;
  is_default_cash?: boolean;
  is_default_bank?: boolean;
  allow_inflows?: boolean;
  allow_outflows?: boolean;
  contact_id?: number | null;
  contact?: {
    id: number;
    name: string;
    document_number?: string;
  } | null;
  is_active?: boolean;
  current_balance?: number;
  created_at?: string;
}

export type TreasuryMovementType =
  | 'INTERNAL_TRANSFER'
  | 'PARTNER_REIMBURSEMENT'
  | 'PARTNER_ADVANCE'
  | 'PARTNER_RETURN'
  | 'PARTNER_LOAN'
  | 'PARTNER_NETTING';

export interface TreasuryMovementItem {
  id: number;
  company_id: number;
  movement_type: TreasuryMovementType;
  source_account_id?: number | null;
  source_account?: {
    id: number;
    name: string;
    type: TreasuryType;
  } | null;
  destination_account_id?: number | null;
  destination_account?: {
    id: number;
    name: string;
    type: TreasuryType;
  } | null;
  contact_id?: number | null;
  contact?: {
    id: number;
    name: string;
    document_number?: string;
  } | null;
  amount: number;
  movement_date: string;
  reference?: string | null;
  notes?: string | null;
  attachments?: TreasuryMovementAttachment[];
  accounting_entry_id?: number | null;
  accounting_entry?: {
    id: number;
    entry_number: string;
    entry_date: string;
  } | null;
  created_at: string;
}

export interface TreasuryMovementAttachment {
  name: string;
  url: string;
  storage_path?: string;
  type?: string;
  size?: number;
}

export interface PartnerBalanceSummary {
  contact_id: number;
  contact_name: string;
  contact_document?: string;
  payable_to_partner: number;
  receivable_from_partner: number;
  net_balance: number;
}

export interface PartnerLedgerMovement {
  id: number;
  entry_id: number;
  entry_number: string;
  entry_date: string;
  concept: string;
  line_description: string;
  account_code: string;
  account_name: string;
  debit: number;
  credit: number;
  running_balance: number;
}

export interface PartnerLedgerResponse {
  company_id: number;
  contact_id: number;
  contact_name: string;
  start_date: string | null;
  end_date: string | null;
  initial_balance: number;
  total_debit: number;
  total_credit: number;
  final_balance: number;
  movements: PartnerLedgerMovement[];
}

/**
 * Determines whether a contact has the PARTNER role or is an active partner account.
 */
export function isPartnerContact(
  contact: any,
  configuredPartnerContactIds?: Set<number>
): boolean {
  if (!contact) return false;

  // 1. If already configured as a PARTNER treasury account
  if (configuredPartnerContactIds?.has(contact.id)) {
    return true;
  }

  // 2. Check roles array or string
  const roles = contact.roles;
  if (roles) {
    if (Array.isArray(roles)) {
      const match = roles.some((r: any) => {
        if (typeof r === 'string') return r.trim().toUpperCase() === 'PARTNER';
        if (r && typeof r === 'object') {
          const val = r.role_type || r.role || r.name || r.key || '';
          return String(val).trim().toUpperCase() === 'PARTNER';
        }
        return false;
      });
      if (match) return true;
    } else if (typeof roles === 'string') {
      if (roles.toUpperCase().includes('PARTNER')) return true;
    }
  }

  // 3. Check role property directly on contact
  if (contact.role && String(contact.role).trim().toUpperCase() === 'PARTNER') {
    return true;
  }

  return false;
}
